import { AuthPrimaryButton, AuthTextField } from "@/components/auth";
import { authNameSchema } from "@/lib/validation/auth";
import { FormEvent, useCallback, useEffect } from "react";

import { zodResolver } from "@hookform/resolvers/zod";
import { useForm } from "react-hook-form";
import { z } from "zod";
import { useStore } from "../../../store/store";
import { handleFormError } from "../../../utils/formErrorUtils";
import { Form, FormRootError } from "../../forms";
import { Button } from "../Button";
import { RouteLink } from "../RouteLink";
import { useToast } from "../Toast";

const profileSchema = z.object({
  firstName: authNameSchema,
  lastName: authNameSchema,
});

type TProfileFormValues = z.infer<typeof profileSchema>;

type TProfileSettingsProps = {
  userData?: {
    id: string;
    first_name: string;
    last_name: string;
    email: string;
    email_verified: boolean;
    phone_verified: boolean;
    two_factor_enabled: boolean;
    status_id: number;
    auth_provider: string;
    phone_number?: string;
    last_login?: string;
  };
};

export const ProfileSettings = ({ userData }: TProfileSettingsProps) => {
  const { toast } = useToast();
  const updateProfile = useStore.use.updateProfile();

  const form = useForm<TProfileFormValues>({
    resolver: zodResolver(profileSchema),
    defaultValues: {
      firstName: userData?.first_name ?? "",
      lastName: userData?.last_name ?? "",
    },
  });

  useEffect(() => {
    if (userData) {
      form.reset({
        firstName: userData.first_name ?? "",
        lastName: userData.last_name ?? "",
      });
    }
  }, [userData, form]);

  const handleSubmit = useCallback(
    async (e: FormEvent<HTMLFormElement>) => {
      e.preventDefault();
      await form.handleSubmit(async (payload) => {
        form.clearErrors();

        // Use store's updateProfile action instead of direct API call
        const result = await updateProfile({
          first_name: payload.firstName,
          last_name: payload.lastName,
        });

        if (result.success) {
          toast({
            variant: "success",
            title: "Profile updated successfully",
          });
        } else {
          // handleFormError attaches field-level errors to inputs and surfaces
          // everything else as a form-level banner (<FormRootError />). When the
          // store only returns a plain message, mirror that into the banner.
          if (result.errorObject) {
            await handleFormError(result.errorObject, form);
          } else {
            form.setError("root", {
              type: "server",
              message: result.error ?? "We couldn't update your profile. Please try again.",
            });
          }
          if (import.meta.env.DEV) {
            console.error("[settings-profile] Profile update failed", {
              error: result.errorObject ?? result.error,
            });
          }
        }
      })();
    },
    [form, toast, updateProfile]
  );

  return (
    <div className="space-y-10">
      <div>
        <h3 className="text-lg font-semibold">Profile</h3>
        <p className="text-sm text-muted-foreground">Update your personal information</p>
      </div>

      <Form {...form}>
        <form
          onSubmit={(e) => {
            void handleSubmit(e);
          }}
          className="space-y-6"
        >
          <FormRootError />

          <div className="grid gap-4">
            <div className="grid gap-4 md:grid-cols-2">
              <AuthTextField
                control={form.control}
                name="firstName"
                label="First name"
                inputProps={{ placeholder: "Enter your name" }}
              />
              <AuthTextField
                control={form.control}
                name="lastName"
                label="Last name"
                inputProps={{ placeholder: "Enter your name" }}
              />
            </div>

            <div className="grid gap-2">
              <div className="space-y-2">
                <label className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70">
                  Email
                </label>
                <div className="flex items-center justify-between rounded-md border border-input bg-background px-3 py-2 text-sm">
                  <span className={userData?.email ? "text-foreground" : "text-muted-foreground"}>
                    {userData?.email ?? "No email"}
                  </span>
                  <RouteLink to="/auth/edit-email">
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      className="h-8 px-4"
                    >
                      Change email
                    </Button>
                  </RouteLink>
                </div>
              </div>
            </div>

            <div className="grid gap-2">
              <div className="space-y-2">
                <label className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70">
                  Phone
                </label>
                <div className="flex items-center justify-between rounded-md border border-input bg-background px-3 py-2 text-sm">
                  <span className={userData?.phone_number ? "text-foreground" : "text-muted-foreground"}>
                    {userData?.phone_number ?? "No phone number"}
                  </span>
                  <RouteLink to="/auth/edit-number">
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      className="h-8 px-4"
                    >
                      Edit number
                    </Button>
                  </RouteLink>
                </div>
              </div>
            </div>
          </div>

          <div className="flex flex-col items-center justify-center gap-3 pt-4 sm:flex-row">
            <AuthPrimaryButton isLoading={form.formState.isSubmitting} disabled={form.formState.isSubmitting}>
              {form.formState.isSubmitting ? "Updating…" : "Update"}
            </AuthPrimaryButton>
            <Button
              type="button"
              variant="outline"
              size="lg"
              className="px-10"
              disabled={form.formState.isSubmitting}
              onClick={() => {
                form.clearErrors();
                form.reset({
                  firstName: userData?.first_name ?? "",
                  lastName: userData?.last_name ?? "",
                });
              }}
            >
              Cancel
            </Button>
          </div>
        </form>
      </Form>

      <div className="space-y-6">
        <div className="rounded-lg border bg-background p-4">
          <div className="text-sm font-semibold">Password</div>
          <div className="mt-1 text-sm text-muted-foreground">
            Password updates are available under the <span className="font-medium text-foreground">Security</span> tab.
          </div>
        </div>
      </div>
    </div>
  );
};
